import React, { useState, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import SEO from "../components/seo"
import Layout from "../components/layout"
import Hero from "../components/organisms/Hero"
import OurValues from "../components/organisms/OurValues"
import HowWeWork from "../components/organisms/HowWeWork"
import OurTeam from "../components/organisms/OurTeam"
import ContactUs from "../components/organisms/ContactUs"
import LearnMore from "../components/organisms/LearnMore"
import Brands from "../components/organisms/Brands"
import Awards from "../components/organisms/Awards"
import Gallery from "../components/organisms/Gallery"
import CTA from "../components/organisms/CTA"

const query = graphql`
  {
    allStrapiMembers(sort: { order: ASC, fields: order }) {
      nodes {
        memberSocials {
          memberBehance
          memberDribbble
          memberLinkedin
        }
        jobTitle
        teamMember {
          Name
        }
        description
        animoji {
          firstFrame {
            childImageSharp {
              original {
                src
              }
            }
          }
          frames {
            childImageSharp {
              original {
                src
              }
            }
          }
        }
        image {
          childImageSharp {
            fluid(maxWidth: 464, quality: 100) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
        imageSmall {
          childImageSharp {
            fluid(maxWidth: 156, quality: 100) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
        name
      }
    }
    strapiAbout {
      gallery {
        imageSingle {
          image {
            childImageSharp {
              fluid(maxWidth: 538, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
      hero {
        header {
          subtitle
          title
        }
        image {
          childImageSharp {
            fluid(maxWidth: 1200, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
      howWeWork {
        header {
          subtitle
          title
        }
        icons {
          svgCode
          title
        }
      }
      ourTeam {
        header {
          subtitle
          title
        }
      }
      CTA {
        LinkText
        LinkURL
        Text
      }
      ourValues {
        description
        header {
          subtitle
          title
        }
        textSlides {
          description
          title
        }
      }
      contactUs {
        title {
          subtitle
          title
        }
        buttons {
          title
        }
      }
      clientsBrands {
        header {
          subtitle
          title
        }
      }
      beforeFooter {
        button {
          title
        }
        text
      }
      AwardsList {
        header {
          subtitle
          title
        }
      }
      metaTags {
        description
        ogImage {
          childImageSharp {
            fluid(maxWidth: 1200, quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        title
      }
    }
    allStrapiClients(sort: { fields: title }) {
      nodes {
        title
        country {
          name
        }
      }
    }
    allStrapiCountries {
      nodes {
        name
      }
    }
    allStrapiYears(sort: { fields: awardsYear, order: DESC }) {
      nodes {
        awardsYear
        awards {
          awardType
          name
          organisation
          year
        }
      }
    }
    allStrapiTeams(sort: { fields: Order, order: ASC }) {
      nodes {
        Name
      }
    }
  }
`

const AboutUsPage = () => {
  const data = useStaticQuery(query)
  const beforeFooter = data.strapiAbout.beforeFooter
  const contactUs = data.strapiAbout.contactUs
  const hero = data.strapiAbout.hero
  const members = data.allStrapiMembers.nodes
  const teams = data.allStrapiTeams.nodes
  const ourValues = data.strapiAbout.ourValues
  const ourTeam = data.strapiAbout.ourTeam
  const cta = data.strapiAbout.CTA
  const clientsBrands = data.strapiAbout.clientsBrands
  const awards = data.strapiAbout.AwardsList
  const clients = data.allStrapiClients.nodes
  let countries = data.allStrapiCountries.nodes
  const awardsList = data.allStrapiYears.nodes
  const howWeWork = data.strapiAbout.howWeWork
  const galleryImages = data.strapiAbout.gallery.imageSingle
  const seo = data.strapiAbout.metaTags
  const [isDesktop, setIsDesktop] = useState(true)

  function checkDevice() {
    if (window.innerWidth > 768) {
      setIsDesktop(true)
    } else {
      setIsDesktop(false)
    }
  }

  useEffect(() => {
    checkDevice()
  }, [])

  useEffect(() => {
    window.addEventListener("resize", checkDevice)
    return () => {
      window.removeEventListener("resize", checkDevice)
    }
  })

  return (
    <Layout className="about-us">
      <SEO
        image={seo?.ogImage?.childImageSharp?.fluid?.src}
        title={seo.title}
        description={seo.description}
      />
      <Hero
        title={hero.header.title}
        subtitle={hero.header.subtitle}
        type="image"
        showClutchRev={false}
        src={hero?.image?.childImageSharp?.fluid}
      />
      <OurValues
        isDesktop={isDesktop}
        subtitle={ourValues.header.subtitle}
        title={ourValues.header.title}
        slides={ourValues.textSlides}
      >
        {ourValues.description}
      </OurValues>
      <Gallery isDesktop={isDesktop} galleryImages={galleryImages} />
      <HowWeWork
        title={howWeWork.header.title}
        subtitle={howWeWork.header.subtitle}
        icons={howWeWork.icons}
      />
      <OurTeam
        title={ourTeam.header.title}
        subtitle={ourTeam.header.subtitle}
        members={members}
        teams={teams}
      />
      <CTA
        text={cta.Text}
        linkText={cta.LinkText}
        linkURL={cta.LinkURL}
      />
      <Brands
        title={clientsBrands.header.title}
        subtitle={clientsBrands.header.subtitle}
        clients={clients}
        countries={countries}
        isDesktop={isDesktop}
      />
      <Awards
        awardsList={awardsList}
        title={awards.header.title}
        subtitle={awards.header.subtitle}
      />
      <ContactUs
        title={contactUs.title.title}
        subtitle={contactUs.title.subtitle}
        contactUsLink={contactUs.buttons[0].link}
        contactUsTitle={contactUs.buttons[0].title}
        callUsLink={contactUs.buttons[1].link}
        callUsTitle={contactUs.buttons[1].title}
      />
      <LearnMore linkTitle={beforeFooter.button.title} to="/work/">
        {beforeFooter.text}
      </LearnMore>
    </Layout>
  )
}

export default AboutUsPage
